import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";
import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { auth, getUserFromDatabase } from "shared/utils/firebase";
import { UserProfileProvider } from "shared/utils/UserProfileContext";
import ResponsiveNavbar from "shared/components/Navbar/ResponsiveNavbar";
import Footer from "shared/components/footer";
import ScrollToTop from "shared/hooks/ScrollTop";
import Auth from "shared/components/Auth/Auth";
import { attachAuthSyncListener } from "shared/utils/sessionBridge";

import AdminDashboard from "./AdminSection/admindash";
import UserManagement from "./AdminSection/UserManagement/UserManagement";
import CourseManagement from "./AdminSection/CourseManagement/CourseManagement";
import EventsManagement from "./AdminSection/EventsManagement/EventsManagement";
import AnnouncementManagement from "./AdminSection/AnnouncementManagement/AnnouncementManagement";
import JobRoleManagement from "./AdminSection/JobRoleManagement/JobRoleManagement";
import CompanyManagement from "./AdminSection/CompanyManagement/CompanyManagement";
import GalleryManagement from "./AdminSection/GalleryManagement/GalleryManagement";
import Settings from "./AdminSection/Settings/Settings";
import AdminProfile from "./AdminSection/AdminProfile/AdminProfile";
import TestManagement from "./AdminSection/TestManagement/TestManagement";
import ProjectManagement from "./AdminSection/ProjectManagement/ProjectManagement";
import ContactRequests from "./AdminSection/ContactRequests/ContactRequests";
import ModuleManagement from "./AdminSection/ReadModuleManagement/ReadModuleManagement";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userDetails, setUserDetails] = useState({});
  const [loading, setLoading] = useState(true);

  const fetchUserDetails = async (uid) => {
    try {
      const details = await getUserFromDatabase(uid);
      setUserDetails(details);
    } catch (error) {
      console.error("Error fetching user details", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const unbindBridge = attachAuthSyncListener(auth);

    const listener = auth.onAuthStateChanged((user) => {
      if (!user) {
        setIsAuthenticated(false);
        setUserDetails({});
        setLoading(false);
        return;
      }
      setIsAuthenticated(true);
      fetchUserDetails(user.uid);
    });

    return () => {
      unbindBridge && unbindBridge();
      listener();
    };
  }, []);

  // Admin access validation (handles both case formats 'Admin' and 'admin')
  const isAdmin = userDetails?.role === 'Admin' || userDetails?.role === 'admin' || userDetails?.isAdmin === true;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "100vh" }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // Component to handle access denied and show modal for logged in users who are not Admins
  function NonAdminRedirect() {
    const getMainLoginUrl = () => {
      const isLocal = window.location.hostname.includes("localhost") || window.location.hostname.includes("127.0.0.1") || window.location.hostname.includes(".local");
      return isLocal 
        ? `http://${window.location.hostname.replace("admin.", "")}:3000/login`
        : "https://siliconlogix.in/login";
    };

    const handleLogout = async () => {
      const { signOut } = await import("firebase/auth");
      await signOut(auth);
    };

    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: "100vh", backgroundColor: "#f8f9fa" }}>
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header border-0">
                <h5 className="modal-title text-danger fw-bold">Access Denied</h5>
              </div>
              <div className="modal-body py-2">
                <p className="fw-semibold text-dark mb-2">You do not have administrative privileges to access this section.</p>
                <p className="text-muted small mb-0">This login is restricted to administrators only. If you are a student or professional, please use the main website portal to access your account.</p>
              </div>
              <div className="modal-footer border-0 d-flex justify-content-between">
                <button className="btn btn-outline-danger btn-sm" onClick={handleLogout}>
                  Log Out
                </button>
                <a className="btn btn-primary btn-sm" href={getMainLoginUrl()}>
                  Go to Main Portal Login
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Router>
        <ScrollToTop>
          <UserProfileProvider initialUserDetails={userDetails} uid={userDetails?.uid}>
            {isAuthenticated && isAdmin && <ResponsiveNavbar auth={isAuthenticated} userDetails={userDetails} />}
            <div className="container-margin"></div>
            <Routes>
              {/* Render Admin dashboard routes only if logged in and admin */}
              {isAuthenticated && isAdmin ? (
                <>
                  <Route path="/admin" element={<AdminDashboard userDetails={userDetails} auth={isAuthenticated} />}>
                    <Route path="users" element={<UserManagement />} />
                    <Route path="courses" element={<CourseManagement />} />
                    <Route path="textmodule" element={<ModuleManagement />} />
                    <Route path="events" element={<EventsManagement />} />
                    <Route path="announcements" element={<AnnouncementManagement />} />
                    <Route path="jobs" element={<JobRoleManagement />} />
                    <Route path="companies" element={<CompanyManagement />} />
                    <Route path="gallery" element={<GalleryManagement />} />
                    <Route path="tests" element={<TestManagement />} />
                    <Route path="projects" element={<ProjectManagement />} />
                    <Route path="contact-requests" element={<ContactRequests />} />
                    <Route path="settings" element={<AdminProfile userDetails={userDetails} auth={isAuthenticated} />} />
                    <Route path="profile" element={<AdminProfile userDetails={userDetails} auth={isAuthenticated} />} />
                  </Route>
                  <Route path="/login" element={<Navigate to="/admin" replace />} />
                  <Route path="/signup" element={<Navigate to="/admin" replace />} />
                  <Route path="/" element={<Navigate to="/admin" replace />} />
                  <Route path="*" element={<Navigate to="/admin" replace />} />
                </>
              ) : isAuthenticated && !isAdmin ? (
                // Logged in but not Admin -> Redirect to main dashboard
                <>
                  <Route path="*" element={<NonAdminRedirect />} />
                </>
              ) : (
                // Logged out -> Render auth page
                <>
                  <Route path="/login" element={<Auth />} />
                  <Route path="/signup" element={<Auth signup />} />
                  <Route path="*" element={<Navigate to="/login" replace />} />
                </>
              )}
            </Routes>
            {isAuthenticated && isAdmin && <Footer />}
          </UserProfileProvider>
        </ScrollToTop>
      </Router >
    </>
  );
}

export default App;
